import React, { memo, useMemo } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import classNames from 'classnames';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { HTMLErrorBoundary } from '@modules/common';
import { WistiaEmbedVideoBlockSettings } from './WistiaEmbedVideoBlockSettings';
import { Session } from '@modules/auth/session';
import { R } from '@core/utils/r';
import './index.scss';

function defineWistiaEmbedVideoBlockForm() {
  return formGroup({
    wistiaVideoId: formControl<string>({
      initialValue: 'Wistia Video ID',
    }),
    fullWdith: formControl<boolean>({
      initialValue: true,
      label: 'Full Width',
    }),
    advance: defineBlockAdvanceForm(),
  });
}

export type WistiaEmbedVideoWidgetFormData = FormDataType<typeof defineWistiaEmbedVideoBlockForm>;
export type WistiaEmbedVideoWidgetForm = ReturnType<typeof defineWistiaEmbedVideoBlockForm>;

export enum WistiaEmbedVideoBlockVariation {
  embedWistiaVideo = 'embedWistiaVideo',
}

export const WistiaEmbedVideoBlock = memo((props: CourseBuilder.BlockProps<WistiaEmbedVideoBlockVariation>) => {
  const { variation } = props;
  const form = useMemo(defineWistiaEmbedVideoBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const user = useObservable(Session.user$);

  useConfigureBlock({
    props,
    form,
    contentComponent: WistiaEmbedVideoBlockSettings,
    contentProps: { form },
  });

  const getUserEmail = () => {
    if (user) {
      return user.email;
    }
    return '';
  };

  const getUserId = () => {
    if (user) {
      return `user_${user.id}`;
    }
    return `user_${Date.now()}`;
  };

  const buildWistiaEmbedUrl = (videoId: string) => {
    const baseUrl = `https://fast.wistia.net/embed/iframe/${videoId}`;

    const params = new URLSearchParams({
      videoFoam: 'true',
      email: getUserEmail(),
      playerColor: '54bbff',
      // Enable tracking (set to 'true' to disable tracking)
      // doNotTrack: 'false',
    });

    return `${baseUrl}?${params.toString()}`;
  };

  const wistiaVideoId = formValue.wistiaVideoId || '';
  const embedUrl = buildWistiaEmbedUrl(wistiaVideoId);

  return (
    <BlockContainer className={classNames('d-flex flex-fill', 'block-embed', variation)} advanceForm={formValue.advance}>
      <HTMLErrorBoundary>
        {R.isEmpty(formValue.wistiaVideoId) && (
          <div className="d-flex justify-content-center align-items-center h-100 p-4">
            <div className="text-center text-muted">Please enter a Wistia Video ID to preview the video.</div>
          </div>
        )}
        {!R.isEmpty(formValue.wistiaVideoId) && (
          <>
            <div className="wistia-video-container" style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
              <div style={{ height: '100%', left: 0, position: 'absolute', top: 0, width: '100%' }}>
                <iframe
                  src={embedUrl}
                  title="Wistia Video"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  frameBorder="0"
                  scrolling="no"
                  className="wistia_embed"
                  name="wistia_embed"
                  style={{ height: '100%', width: '100%' }}
                  data-user-id={getUserId()}
                />
              </div>
            </div>
            <script src="https://fast.wistia.net/assets/external/E-v1.js" async />
          </>
        )}
      </HTMLErrorBoundary>
    </BlockContainer>
  );
});
